// themes.ts
import { createTheme, Shadows } from "@mui/material/styles";

// ==================== AG GRID THEME VARIABLES ====================
export const agDarkThemeParams = {
  '--ag-background-color': '#3d464dff',
  '--ag-foreground-color': '#F8F9FA',
  '--ag-card-background-color': '#343A40',
  '--ag-border-color': 'rgba(255, 255, 255, 0.08)',
  '--ag-row-border-color': 'rgba(255, 255, 255, 0.08)',
  '--ag-header-background-color': '#343A40',
  '--ag-header-foreground-color': '#80CC00',
  '--ag-accent-color': '#80CC00',
  '--ag-selected-row-background-color': 'rgba(128,204,0,0.10)',
  '--ag-row-hover-color': 'rgba(255,255,255,0.05)',
  '--ag-secondary-foreground-color': '#ADB5BD',
  '--ag-input-background-color': '#212529',
  '--ag-input-border-color': 'rgba(255,255,255,0.15)',
  '--ag-input-focus-border-color': '#80CC00',
  '--ag-font-family': "'Roboto', 'Arial', sans-serif",
  '--ag-font-weight': '300',
  '--ag-header-font-weight': '400',
  '--ag-row-font-weight': '300',
  '--ag-data-font-weight': '300',
  '--ag-label-font-weight': '300',
  '--ag-link-color': '#A0C4FF',
  '--ag-link-hover-color': '#8EAFE0',
  '--ag-font-size': '0.75rem', // più compatta
};

export const agLightThemeParams = {
  '--ag-background-color': '#ebe8e8ff',
  '--ag-foreground-color': '#212529',
  '--ag-card-background-color': '#F8F9FA',
  '--ag-border-color': 'rgba(0,0,0,0.08)',
  '--ag-row-border-color': 'rgba(0,0,0,0.08)',
  '--ag-header-background-color': '#E9ECEF',
  '--ag-header-foreground-color': '#80CC00',
  '--ag-accent-color': '#80CC00',
  '--ag-selected-row-background-color': 'rgba(128,204,0,0.10)',
  '--ag-row-hover-color': 'rgba(0,0,0,0.03)',
  '--ag-secondary-foreground-color': '#495057',
  '--ag-input-background-color': '#FFFFFF',
  '--ag-input-border-color': 'rgba(0,0,0,0.15)',
  '--ag-input-focus-border-color': '#80CC00',
  '--ag-font-family': "'Roboto', 'Arial', sans-serif",
  '--ag-font-weight': '300',
  '--ag-header-font-weight': '400',
  '--ag-row-font-weight': '300',
  '--ag-data-font-weight': '300',
  '--ag-label-font-weight': '300',
  '--ag-link-color': '#339AF0',
  '--ag-link-hover-color': '#1C7ED6',
  '--ag-font-size': '0.75rem',
};

// ==================== MUI DARK THEME ====================
const darkThemeCustom = createTheme({
  palette: {
    mode: "dark",
    primary: { main: "#80CC00", contrastText: "#000000" },
    secondary: { main: "#FFB7A0", contrastText: "#000000" },
    background: { default: "#212529", paper: "#343A40" },
    text: { primary: "#F8F9FA", secondary: "#ADB5BD" },
    divider: "rgba(255, 255, 255, 0.08)",
  },
  typography: {
    fontFamily: "'Roboto', 'Arial', sans-serif",
    fontWeightLight: 300,
    fontWeightRegular: 300,
    fontWeightMedium: 400,
    fontWeightBold: 500,
    h1: { color: "#80CC00", fontSize: "1.8rem", fontWeight: 400 },
    h2: { color: "#80CC00", fontSize: "1.5rem", fontWeight: 400 },
    h3: { color: "#80CC00", fontSize: "1.25rem", fontWeight: 400 },
    h4: { color: "#80CC00", fontSize: "1.125rem", fontWeight: 400 },
    h5: { color: "#80CC00" },
    h6: { color: "#80CC00" },
    body1: { fontSize: "0.75rem", fontWeight: 300 },
    body2: { fontSize: "0.75rem", fontWeight: 300 },
    button: { textTransform: "none", fontWeight: 300, fontSize: "0.75rem" },
  },
  shape: { borderRadius: 4 },
  shadows: Array(25).fill("none") as Shadows,
  components: {
    MuiLink: { defaultProps: { color: "#A0C4FF" } },
    MuiPaper: { styleOverrides: { root: { backgroundImage: "none", boxShadow: "none", border: "1px solid rgba(255,255,255,0.08)" } } },
    MuiCard: { styleOverrides: { root: { boxShadow: "none", border: "1px solid rgba(255,255,255,0.08)" } } },
    MuiButton: {
      styleOverrides: {
        root: { boxShadow: "none", borderRadius: 4, padding: "8px 20px", "&:hover": { boxShadow: "none" } },
        containedPrimary: { backgroundColor: "#80CC00", color: "#000", "&:hover": { backgroundColor: "#65A300", color: "#000" } },
        containedSecondary: { backgroundColor: "#FFB7A0", color: "#000", "&:hover": { backgroundColor: "#FFAC90", color: "#000" } },
        outlinedPrimary: { borderColor: "#80CC00", color: "#80CC00", "&:hover": { backgroundColor: "rgba(128,204,0,0.08)", borderColor: "#65A300" } },
        outlinedSecondary: { borderColor: "#FFB7A0", color: "#FFB7A0", "&:hover": { backgroundColor: "rgba(255,183,160,0.08)", borderColor: "#FFAC90" } },
      },
    },
    MuiTableCell: { styleOverrides: { root: { fontSize: "0.75rem", fontWeight: 300, padding: "12px 16px", borderBottom: "1px solid rgba(255,255,255,0.08)" }, head: { color: "#80CC00", fontWeight: 400 } } },
  },
});

// ==================== EXPORT THEME WITH CSS VARS ====================
const themeWithCssVars = {
  ...darkThemeCustom,
  cssVars: {
    "--color-primary": darkThemeCustom.palette.primary.main,
    "--color-secondary": darkThemeCustom.palette.secondary.main,
    "--color-background": darkThemeCustom.palette.background.default,
    "--color-paper": darkThemeCustom.palette.background.paper,
    "--color-text-primary": darkThemeCustom.palette.text.primary,
    "--color-text-secondary": darkThemeCustom.palette.text.secondary,
    "--color-button": darkThemeCustom.palette.primary.main,
  },
};

export default themeWithCssVars;

/*
import { createTheme, Shadows } from "@mui/material/styles";

const darkThemeCustom = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#A0C4FF",
      contrastText: "#000000",
    },
    secondary: {
      main: "#FFB7A0",
      contrastText: "#000000",
    },
    background: {
      default: "#212529",
      paper: "#343A40",
    },
    text: {
      primary: "#F8F9FA",
      secondary: "#ADB5BD",
    },
    divider: "rgba(255, 255, 255, 0.08)",
  },
  typography: {
    fontFamily: "'Inter', 'Lato', 'Arial', sans-serif",
    h1: {
      fontSize: "2.25rem",
      fontWeight: 600,
    },
    button: {
      textTransform: "none",
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 4,
  },
  shadows: Array(25).fill("none") as Shadows,
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
          boxShadow: "none",
          border: "1px solid rgba(255, 255, 255, 0.08)",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          boxShadow: "none",
          borderRadius: 4,
          padding: "8px 20px",
          "&:hover": {
            boxShadow: "none",
          },
        },        containedPrimary: {
          backgroundColor: "#A0C4FF",
          color: "#000000",
          "&:hover": {
            backgroundColor: "#8EAFE0",
            color: "#000000",
          },
        },        containedSecondary: {
          backgroundColor: "#FFB7A0",
          color: "#000000",
          "&:hover": {
            backgroundColor: "#FFAC90",
            color: "#000000",
          },
        },
        outlinedPrimary: {
          borderColor: "#A0C4FF",
          color: "#A0C4FF",
          "&:hover": {
            backgroundColor: "rgba(160, 196, 255, 0.08)",
            borderColor: "#8EAFE0",
          },
        },
        outlinedSecondary: {
          borderColor: "#FFB7A0",
          color: "#FFB7A0",
          "&:hover": {
            backgroundColor: "rgba(255, 183, 160, 0.08)",
            borderColor: "#FFAC90",
          },
        },
        textPrimary: {
          color: "#A0C4FF",
          "&:hover": {
            backgroundColor: "rgba(160, 196, 255, 0.08)",
          },
        },
        textSecondary: {
          color: "#FFB7A0",
          "&:hover": {
            backgroundColor: "rgba(255, 183, 160, 0.08)",
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: "none",
          border: "1px solid rgba(255, 255, 255, 0.08)",
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: "none",
          backgroundColor: "#343A40",
          borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          boxShadow: "none",
          backgroundColor: "#212529",
          borderRight: "1px solid rgba(255, 255, 255, 0.08)",
        },
      },
    },    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.05)",
            boxShadow: "none",
          },
          "&:focus": {
            boxShadow: "none",
          },
          // Standardize edit icon colors to blue across all components
          "& .MuiSvgIcon-root": {
            "&[data-testid='EditIcon']": {
              color: "#A0C4FF", // primary.main color
            },
          },
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          "&:hover": {
            boxShadow: "none",
          },
          "&:focus": {
            boxShadow: "none",
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          "&:hover": {
            boxShadow: "none",
          },
          "&:focus": {
            boxShadow: "none",
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: "0.925rem", // Increased from 0.875rem or default
          padding: "12px 16px", // Adjusted padding for more space
          borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
        },
        head: {
          fontWeight: 600,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(255, 255, 255, 0.15)",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(255, 255, 255, 0.3)",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#A0C4FF",
          },
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          borderTopLeftRadius: 4,
          borderTopRightRadius: 4,
          backgroundColor: "rgba(255, 255, 255, 0.05)",
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.08)",
          },
          "&.Mui-focused": {
            backgroundColor: "rgba(255, 255, 255, 0.08)",
          },
          "&:before": {
            borderBottom: "none !important",
          },
          "&:after": {
            borderBottom: "none !important",
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: "#ADB5BD",
        },
        outlined: {
          "&.Mui-focused": {
            color: "#A0C4FF",
          },
        },
      },
    },
  },
});

const themeWithCssVars = {
  ...darkThemeCustom,
  cssVars: {
    "--color-primary": darkThemeCustom.palette.primary.main,
    "--color-secondary": darkThemeCustom.palette.secondary.main,
    "--color-background": darkThemeCustom.palette.background.default,
    "--color-paper": darkThemeCustom.palette.background.paper,
    "--color-text-primary": darkThemeCustom.palette.text.primary,
    "--color-text-secondary": darkThemeCustom.palette.text.secondary,
    "--color-button": darkThemeCustom.palette.primary.main,
  },
};

export default themeWithCssVars;
*/